import {
  createColumnHelper,
  createExpandedRowModel,
  rowExpandingFeature,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import type { ExpandedState } from '@tanstack/react-table';
import { SubscriptionType, type Subscription, type User, UserRole } from '@whispers/sdk';
import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { useInfiniteUserReadings, useUpdateUser } from '../../api.js';
import { Button } from '../Button.js';
import { Input } from '../Input.js';

type UserTableProps = {
  users: User[];
  subscriptionsByUserId: Record<string, Subscription[]>;
  isLoading: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  emailFilter: string;
  onEmailFilterChange: (value: string) => void;
};

const features = tableFeatures({
  rowExpandingFeature,
  expandedRowModel: createExpandedRowModel(),
});

const columnHelper = createColumnHelper<typeof features, User>();

const formatDate = (date: Date) =>
  new Date(date).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatEnum = (value: string) =>
  value
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

type SelectOption<T extends string> = { value: T; label: string };

const getRoleOptions = (currentRole?: UserRole): SelectOption<UserRole | ''>[] => [
  { value: '', label: 'No role' },
  ...Object.values(UserRole.enum)
    .filter((role) => role !== UserRole.enum.ADMIN || role === currentRole)
    .map((value) => ({ value, label: formatEnum(value) })),
];

const getSubscriptionTypeOptions = (
  currentSubscriptionType: SubscriptionType,
): SelectOption<SubscriptionType>[] =>
  Object.values(SubscriptionType.enum)
    .filter((type) => type !== SubscriptionType.enum.PAID || type === currentSubscriptionType)
    .map((value) => ({ value, label: formatEnum(value) }));

type SelectFieldProps<T extends string> = {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  disabled?: boolean;
};

const selectChevron =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%234a4035' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")";

const SelectField = <T extends string>({
  label,
  value,
  onChange,
  options,
  disabled,
}: SelectFieldProps<T>) => (
  <label className="grid gap-1.5">
    <span className="text-xs text-text-secondary">{label}</span>
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const option = options.find((o) => o.value === e.target.value);
        if (option) {
          onChange(option.value);
        }
      }}
      style={{
        backgroundImage: selectChevron,
        backgroundPosition: 'right 0.75rem center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '1rem',
      }}
      className="w-full cursor-pointer appearance-none rounded-lg border border-border bg-background py-2 pr-9 pl-3 text-sm text-text focus:border-button-outline-border focus:outline-none focus:ring-1 focus:ring-focus-ring disabled:cursor-not-allowed disabled:bg-disabled-bg disabled:text-disabled-text"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

type EditUserFormProps = { user: User };

const EditUserForm = ({ user }: EditUserFormProps) => {
  const updateUser = useUpdateUser();
  const [role, setRole] = useState<UserRole | ''>(user.role ?? '');
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType>(user.subscriptionType);

  useEffect(() => {
    setRole(user.role ?? '');
    setSubscriptionType(user.subscriptionType);
  }, [user.role, user.subscriptionType]);

  const hasChanges = role !== (user.role ?? '') || subscriptionType !== user.subscriptionType;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasChanges || updateUser.isPending) {
      return;
    }
    updateUser.mutate({
      userId: user.id,
      role: role === '' ? null : role,
      subscriptionType,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
      <SelectField
        label="Role"
        value={role}
        onChange={setRole}
        options={getRoleOptions(user.role)}
        disabled={updateUser.isPending}
      />
      <SelectField
        label="Subscription"
        value={subscriptionType}
        onChange={setSubscriptionType}
        options={getSubscriptionTypeOptions(user.subscriptionType)}
        disabled={updateUser.isPending}
      />
      <Button type="submit" size="sm" disabled={!hasChanges || updateUser.isPending}>
        {updateUser.isPending ? 'Saving…' : 'Save'}
      </Button>
      {updateUser.isError && (
        <p className="text-sm text-error sm:col-span-3">
          {updateUser.error instanceof Error ? updateUser.error.message : 'Failed to update user.'}
        </p>
      )}
      {updateUser.isSuccess && !hasChanges && (
        <p className="text-sm text-success sm:col-span-3">Changes saved</p>
      )}
    </form>
  );
};

// Inline SVG chevron — rotates 90° when expanded
const Chevron = ({ expanded }: { expanded: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0 transition-transform duration-200"
    style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const columns = columnHelper.columns([
  columnHelper.display({
    id: 'expander',
    header: () => null,
    cell: ({ row }) => (
      <button
        onClick={(e) => {
          e.stopPropagation();
          row.toggleExpanded();
        }}
        className="flex items-center justify-center rounded p-0.5 text-text-secondary hover:text-text"
        aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
      >
        <Chevron expanded={row.getIsExpanded()} />
      </button>
    ),
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: (info) => <span className="text-text">{info.getValue()}</span>,
  }),
  columnHelper.accessor('subscriptionType', {
    header: 'Subscription',
    cell: (info) => (
      <span className="text-sm text-text-secondary">{formatEnum(info.getValue())}</span>
    ),
  }),
  columnHelper.accessor('role', {
    header: 'Role',
    cell: (info) => {
      const role = info.getValue();
      return role ? (
        <span className="rounded-full bg-border px-2 py-0.5 text-xs font-medium text-text">
          {formatEnum(role)}
        </span>
      ) : null;
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Registered',
    cell: (info) => (
      <span className="text-sm text-text-secondary">{formatDate(info.getValue())}</span>
    ),
  }),
]);

type DetailFieldProps = { label: string; value: string | undefined | null };

const DetailField = ({ label, value }: DetailFieldProps) =>
  value ? (
    <div>
      <dt className="text-xs text-text-secondary">{label}</dt>
      <dd className="mt-0.5 text-sm text-text">{value}</dd>
    </div>
  ) : null;

type ExpandedPanelProps = { user: User; subscriptions: Subscription[] };

const ExpandedPanel = ({ user, subscriptions }: ExpandedPanelProps) => {
  const sorted = [...subscriptions].toSorted(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const {
    data: readingsPages,
    isLoading: readingsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteUserReadings(user.id);

  const allReadings = readingsPages?.pages.flatMap((p) => p.readings) ?? [];

  return (
    <div className="grid gap-6">
      {/* Access */}
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
          Access
        </p>
        <EditUserForm user={user} />
      </div>

      {/* Account */}
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
          Account
        </p>
        <dl className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Email" value={user.email} />
          <DetailField label="Registered" value={formatDate(user.createdAt)} />
          <DetailField label="Acquisition" value={formatEnum(user.acquisitionType)} />
          <div>
            <dt className="text-xs text-text-secondary">ID</dt>
            <dd className="mt-0.5 font-mono text-xs text-text-secondary">{user.id}</dd>
          </div>
        </dl>
      </div>

      {/* Readings */}
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
          Readings
        </p>
        {readingsLoading ? (
          <p className="text-sm text-text-secondary">Loading…</p>
        ) : allReadings.length === 0 ? (
          <p className="text-sm text-text-secondary">No readings</p>
        ) : (
          <div className="grid gap-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-1.5 text-left text-xs font-medium text-text-secondary">Date</th>
                  <th className="pb-1.5 text-left text-xs font-medium text-text-secondary">
                    Reading ID
                  </th>
                  <th className="pb-1.5 text-left text-xs font-medium text-text-secondary" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {allReadings.map((reading) => (
                  <tr key={reading.id}>
                    <td className="py-2 text-sm text-text">{formatDate(reading.createdAt)}</td>
                    <td className="py-2 font-mono text-xs text-text-secondary">{reading.id}</td>
                    <td className="py-2 text-right">
                      <Link
                        to={`/readings/${reading.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-text-secondary hover:text-text"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View ↗
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasNextPage && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void fetchNextPage();
                }}
                disabled={isFetchingNextPage}
                className="mt-1 text-left text-xs text-text-secondary hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Subscriptions */}
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
          Stripe Subscriptions{sorted.length > 0 ? ` (${sorted.length})` : ''}
        </p>
        {sorted.length === 0 ? (
          <p className="text-sm text-text-secondary">No subscriptions</p>
        ) : (
          <div className="grid gap-4">
            {sorted.map((sub) => (
              <div key={sub.id} className="rounded-lg border border-border p-4">
                <dl className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  <DetailField label="Status" value={formatEnum(sub.status)} />
                  <DetailField label="Quantity" value={String(sub.quantity)} />
                  <DetailField
                    label="Cancel at period end"
                    value={sub.cancelAtPeriodEnd ? 'Yes' : 'No'}
                  />
                  <DetailField
                    label="Current period start"
                    value={sub.currentPeriodStart ? formatDate(sub.currentPeriodStart) : undefined}
                  />
                  <DetailField
                    label="Current period end"
                    value={sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : undefined}
                  />
                  <DetailField
                    label="Trial start"
                    value={sub.trialStart ? formatDate(sub.trialStart) : undefined}
                  />
                  <DetailField
                    label="Trial end"
                    value={sub.trialEnd ? formatDate(sub.trialEnd) : undefined}
                  />
                  <DetailField
                    label="Cancel at"
                    value={sub.cancelAt ? formatDate(sub.cancelAt) : undefined}
                  />
                  <DetailField
                    label="Canceled at"
                    value={sub.canceledAt ? formatDate(sub.canceledAt) : undefined}
                  />
                  <DetailField
                    label="Ended at"
                    value={sub.endedAt ? formatDate(sub.endedAt) : undefined}
                  />
                  <div>
                    <dt className="text-xs text-text-secondary">Stripe subscription ID</dt>
                    <dd className="mt-0.5 font-mono text-xs text-text-secondary">
                      {sub.stripeSubscriptionId}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-text-secondary">Stripe customer ID</dt>
                    <dd className="mt-0.5 font-mono text-xs text-text-secondary">
                      {sub.stripeCustomerId}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-text-secondary">Stripe price ID</dt>
                    <dd className="mt-0.5 font-mono text-xs text-text-secondary">
                      {sub.stripePriceId}
                    </dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const UserTable = ({
  users,
  subscriptionsByUserId,
  isLoading,
  hasNextPage,
  onLoadMore,
  isLoadingMore,
  emailFilter,
  onEmailFilterChange,
}: UserTableProps) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const table = useTable({
    features,
    columns,
    data: users,
    getRowCanExpand: () => true,
    state: { expanded },
    onExpandedChange: (updater) => {
      setExpanded((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        // Enforce single-row expansion
        const nextKeys = Object.keys(next).filter((k) => (next as Record<string, boolean>)[k]);
        const prevKeys = new Set(
          Object.keys(prev).filter((k) => (prev as Record<string, boolean>)[k]),
        );
        const newlyExpanded = nextKeys.find((k) => !prevKeys.has(k));
        if (newlyExpanded) {
          return { [newlyExpanded]: true };
        }
        return next;
      });
    },
  });

  return (
    <div className="grid gap-4">
      {/* Email filter */}
      <Input
        placeholder="Filter by email…"
        value={emailFilter}
        onChange={(e) => onEmailFilterChange(e.target.value)}
        className="max-w-sm"
      />

      {/* Table */}
      <div
        className={`overflow-x-auto rounded-lg border border-border transition-opacity duration-300 ${isLoading ? 'opacity-40' : 'opacity-100'}`}
      >
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-2.5 text-left text-xs font-medium text-text-secondary"
                  >
                    {header.isPlaceholder ? null : <table.FlexRender header={header} />}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-border">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-sm text-text-secondary"
                >
                  No users found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <tr
                    className="cursor-pointer transition-colors hover:bg-border/20"
                    onClick={() => row.toggleExpanded()}
                  >
                    {row.getAllCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3">
                        <table.FlexRender cell={cell} />
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr className="bg-border/10">
                      <td colSpan={columns.length} className="px-6 py-5">
                        <ExpandedPanel
                          user={row.original}
                          subscriptions={subscriptionsByUserId[row.original.id] ?? []}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {hasNextPage && (
        <div className="flex justify-center">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="rounded-lg border border-border px-4 py-2 text-sm text-text-secondary transition-colors hover:border-text hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};
